// src/mobile/sub-navigation.ts
import { EventManager, setupEventListenersIfNeeded } from './event-manager';
import { SubNavigationItem } from './types';
import { postJsMessage } from '../../../bridge';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';
import API from '../../../api';
import { isAvailable } from 'jssdk/utils';
import { PostMessageOption } from '../../../types';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.setSubNavigationConfig
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function setSubNavigationConfig(
  config: {
    showSubNavigation: boolean;
    items?: SubNavigationItem[] | null;
  },
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<unknown>> {
  await isAvailable('mobile.setSubNavigationConfig');
  setupEventListenersIfNeeded();

  const eventManager = EventManager.getInstance();
  const items = config.items?.map(({ onTap, ...item }) => {
    if (onTap) {
      eventManager.unregisterCallback(
        'onSubNavigationItemClick',
        item.actionID
      );
      eventManager.registerCallback(
        'onSubNavigationItemClick',
        item.actionID,
        onTap
      );
    }
    return item;
  });

  return postJsMessage(
    API.setSubNavigationConfig,
    {
      ...config,
      ...(items && { items }),
    },
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.updateSubNavigationItem
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function updateSubNavigationItem(
  actionID: string,
  update: Partial<SubNavigationItem>,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<unknown>> {
  await isAvailable('mobile.updateSubNavigationItem');
  setupEventListenersIfNeeded();

  if (update.onTap) {
    // Cleanup old callback
    EventManager.getInstance().unregisterCallback(
      'onSubNavigationItemClick',
      actionID
    );

    EventManager.getInstance().registerCallback(
      'onSubNavigationItemClick',
      actionID,
      update.onTap
    );
    delete update.onTap;
  }

  return postJsMessage(
    API.updateSubNavigationItem,
    {
      actionID,
      update,
    },
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.insertSubNavigationItem
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.6.0
 * android    6.6.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function insertSubNavigationItem(
  index: number,
  item: SubNavigationItem,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<unknown>> {
  await isAvailable('mobile.insertSubNavigationItem');
  setupEventListenersIfNeeded();

  const eventManager = EventManager.getInstance();
  const { onTap, ...itemWithoutOnTap } = item;

  if (onTap) {
    eventManager.unregisterCallback('onSubNavigationItemClick', item.actionID);
    eventManager.registerCallback(
      'onSubNavigationItemClick',
      item.actionID,
      onTap
    );
  }

  return postJsMessage(
    API.insertSubNavigationItem,
    {
      index,
      item: itemWithoutOnTap,
    },
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.removeSubNavigationItem
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.6.0
 * android    6.6.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function removeSubNavigationItem(
  actionID: string,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<unknown>> {
  await isAvailable('mobile.removeSubNavigationItem');
  setupEventListenersIfNeeded();

  const eventManager = EventManager.getInstance();
  eventManager.unregisterCallback('onSubNavigationItemClick', actionID);

  return postJsMessage(API.removeSubNavigationItem, { actionID }, options);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.setSelectedSubNavigationItem
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function setSelectedSubNavigationItem(
  actionID: string,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<unknown>> {
  await isAvailable('mobile.setSelectedSubNavigationItem');
  setupEventListenersIfNeeded();
  return postJsMessage(API.setSelectedSubNavigationItem, { actionID }, options);
}
